import React, { useCallback, useEffect, useState } from 'react';

import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { BusinessPublicComplaintsAPI } from '../../lib/api';
import { notify } from '../../lib/notifications';

/**
 * Business → Public Complaints.
 *
 * Org admins enable self-registration at ``/complaints`` for the public
 * (complaints-only auretics-users accounts) and optionally attach
 * Google reCAPTCHA keys.  Captcha is enforced only when both keys are
 * saved.  The secret is write-only — GET reports whether it is set.
 */
export default function BusinessPublicComplaints() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [enabled, setEnabled] = useState(false);
  const [siteKey, setSiteKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [clearSecret, setClearSecret] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await BusinessPublicComplaintsAPI.get();
      setStatus(payload);
      setEnabled(Boolean(payload.public_complaint_login_enabled));
      setSiteKey(payload.recaptcha_site_key || '');
      setSecretKey('');
      setClearSecret(false);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        public_complaint_login_enabled: enabled,
        recaptcha_site_key: siteKey,
      };
      if (clearSecret) {
        body.recaptcha_secret_key = '';
      } else if (secretKey.trim()) {
        body.recaptcha_secret_key = secretKey.trim();
      }
      const payload = await BusinessPublicComplaintsAPI.update(body);
      setStatus(payload);
      setSecretKey('');
      setClearSecret(false);
      notify.success('Public complaint access saved.');
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }, [enabled, siteKey, secretKey, clearSecret]);

  if (loading && !status) return <Loader label="Loading public complaint settings…" />;

  const captchaOn = Boolean(status?.captcha_enabled);
  const secretSet = Boolean(status?.recaptcha_secret_set);

  return (
    <>
      <PageHeader
        title="📣 Public Complaints"
        subtitle="Let the public create a complaints-only account at your organisation's /complaints link."
      />

      <ErrorBox error={error} />

      <section className="ty-card">
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>Public login</h2>
        <p style={{ color: 'var(--ty-muted)', marginTop: 0, marginBottom: 16 }}>
          When this is on, anyone with the link can create an external
          (auretics-users) account and file a complaint. They cannot see
          other modules or edit tickets — the same access as Auretics
          portal users.
        </p>
        <label
          htmlFor="pc-enabled"
          className="ty-row"
          style={{ gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}
        >
          <input
            id="pc-enabled"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            style={{ marginTop: 4 }}
          />
          <span>
            <strong>Enable public login and registration</strong>
            <span style={{ display: 'block', fontSize: 13, color: 'var(--ty-muted)', marginTop: 2 }}>
              Off by default. Staff login at /login is unchanged.
            </span>
          </span>
        </label>

        {status?.public_url ? (
          <div className="ty-field" style={{ marginTop: 16 }}>
            <label htmlFor="pc-url">Public complaints URL</label>
            <input
              id="pc-url"
              className="ty-input"
              type="text"
              readOnly
              value={status.public_url}
              onFocus={(e) => e.target.select()}
            />
            <span style={{ fontSize: 12, color: 'var(--ty-muted)' }}>
              Share this link after enabling public login.
            </span>
          </div>
        ) : null}
      </section>

      <section className="ty-card" style={{ marginTop: 14 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>Captcha</h2>
        <p style={{ color: 'var(--ty-muted)', marginTop: 0, marginBottom: 16 }}>
          Optional. If both a site key and a secret key are saved, the
          public sign-in and create-account forms require Google reCAPTCHA
          v3. Leave them blank to skip captcha.
        </p>
        <p style={{ fontSize: 13, marginTop: 0 }}>
          Status:{' '}
          <strong>{captchaOn ? 'Captcha is active' : 'Captcha is off'}</strong>
          {secretSet && !captchaOn ? ' (secret is stored; add a site key to activate)' : null}
        </p>
        <div className="ty-field">
          <label htmlFor="pc-site-key">reCAPTCHA site key</label>
          <input
            id="pc-site-key"
            className="ty-input"
            type="text"
            autoComplete="off"
            value={siteKey}
            onChange={(e) => setSiteKey(e.target.value)}
            placeholder="6L…"
          />
        </div>
        <div className="ty-field">
          <label htmlFor="pc-secret-key">
            reCAPTCHA secret key {secretSet ? '(saved — enter a new value to replace)' : ''}
          </label>
          <input
            id="pc-secret-key"
            className="ty-input"
            type="password"
            autoComplete="new-password"
            value={secretKey}
            onChange={(e) => {
              setSecretKey(e.target.value);
              if (e.target.value) setClearSecret(false);
            }}
            placeholder={secretSet ? '••••••••' : ''}
            disabled={clearSecret}
          />
        </div>
        <label
          htmlFor="pc-clear-secret"
          className="ty-row"
          style={{ gap: 8, cursor: 'pointer', marginBottom: 16 }}
        >
          <input
            id="pc-clear-secret"
            type="checkbox"
            checked={clearSecret}
            onChange={(e) => {
              setClearSecret(e.target.checked);
              if (e.target.checked) setSecretKey('');
            }}
          />
          <span>Remove saved secret key</span>
        </label>
      </section>

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="ty-btn"
          onClick={save}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
        <button
          type="button"
          className="ty-btn ty-btn--ghost"
          onClick={reload}
          disabled={saving || loading}
        >
          Discard
        </button>
      </div>
    </>
  );
}
